import type { ImageGenParams } from '../types/timeline'

/** 轮次快照里需要回传给上游的白名单扩展参数（与各平台 allowed-option-keys 对齐） */
const SNAPSHOT_EXTRA_KEYS = [
  'quality',
  'format',
  'style',
  'background',
  'moderation',
  'output_format',
  'output_compression',
  'thinking',
  'resolution',
  'input_fidelity',
] as const

/**
 * 从时间线轮次的参数快照还原「提交用」的额外参数。
 *
 * 追加生成 / 重新生成必须以旧轮次为准：直接用页面表单的当前值会出现
 * 「界面标注 9:16、实际按 16:9 或 auto 出图」的比例错乱。
 */
export function extraParamValuesFromSnapshot(
  params?: ImageGenParams | null,
): Record<string, string> {
  const out: Record<string, string> = {}
  if (!params) return out
  const raw = params as unknown as Record<string, unknown>
  for (const key of SNAPSHOT_EXTRA_KEYS) {
    const value = raw[key]
    if (value === undefined || value === null) continue
    const text = String(value).trim()
    if (!text || text === 'auto') continue
    out[key] = text
  }
  if (typeof params.n === 'number' && params.n >= 1) {
    out.n = String(Math.floor(params.n))
  }
  return out
}

export type SnapshotDimensionResolution =
  | { ok: true; dimensionMode: 'size' | 'aspect_ratio'; aspectRatio: string; sizeValue: string }
  | { ok: false; message: string }

export type SnapshotDimensionOptions = {
  /** 旧轮次的参数快照 */
  snapshot?: ImageGenParams | null
  /** 当前模型（以模型目录实时定义为准）的画幅模式 */
  modelDimensionMode: 'size' | 'aspect_ratio'
  /** 当前模型可选比例（aspect_ratio 模式） */
  aspectOptions?: readonly { value: string }[] | null
  /** 当前模型可选尺寸（size 模式） */
  sizeOptions?: readonly { value: string; previewWidth: number; previewHeight: number }[] | null
}

/** 宽高比匹配容差（百分比）：1920×1088 与 16:9 差 0.09%，视为同一比例 */
const RATIO_TOLERANCE = 0.02

function parseRatioValue(value: string): number | null {
  const m = value.trim().match(/^(\d+)\s*[:：]\s*(\d+)$/)
  if (!m) return null
  const w = parseInt(m[1]!, 10)
  const h = parseInt(m[2]!, 10)
  if (!(w > 0 && h > 0)) return null
  return w / h
}

/** 由像素宽高在候选比例中找最接近的一项（容差内），找不到返回空串 */
export function deriveAspectRatioFromDimensions(
  width: number,
  height: number,
  options?: readonly { value: string }[] | null,
): string {
  if (!(width > 0 && height > 0)) return ''
  const target = width / height
  let best: { value: string; diff: number } | null = null
  for (const opt of options ?? []) {
    if (opt.value === 'auto') continue
    const ratio = parseRatioValue(opt.value)
    if (ratio == null) continue
    const diff = Math.abs(target - ratio) / ratio
    if (diff <= RATIO_TOLERANCE && (!best || diff < best.diff)) {
      best = { value: opt.value, diff }
    }
  }
  return best?.value ?? ''
}

/** 由像素宽高在候选尺寸中找预览宽高完全一致的一项，找不到返回空串 */
export function deriveSizeValueFromDimensions(
  width: number,
  height: number,
  options?: readonly { value: string; previewWidth: number; previewHeight: number }[] | null,
): string {
  if (!(width > 0 && height > 0)) return ''
  for (const opt of options ?? []) {
    if (opt.previewWidth === width && opt.previewHeight === height) return opt.value
  }
  return ''
}

/**
 * 追加生成 / 重新生成前，把「旧轮次画幅快照」解析成「本次请求」应使用的画幅参数。
 *
 * 模型定义可能已在两次生成之间被管理员调整（如 QIQI GPT-Image-2 从 size 模式切到
 * aspect_ratio 模式）：此时上游会静默丢弃已不再发送的画幅参数，按默认画幅出图
 * （表现为界面标 16:9、实际出 9:16）。因此这里绝不允许「快照没有当前模式参数就发空」：
 * - 模式一致：直接沿用快照值；
 * - 快照是精确尺寸、模型已改为比例模式：由快照宽高推导最接近的候选比例；
 * - 快照是比例、模型已改为尺寸模式：由快照宽高匹配候选尺寸；
 * - 都推导不出：显式失败，绝不静默跑偏。
 */
export function resolveSnapshotDimensionForResubmit(
  args: SnapshotDimensionOptions,
): SnapshotDimensionResolution {
  const { snapshot, modelDimensionMode, aspectOptions, sizeOptions } = args

  const snapshotAspect = snapshot?.aspect_ratio?.trim() ?? ''
  const snapshotSize = snapshot?.size?.trim() ?? ''
  const snapshotW = typeof snapshot?.width === 'number' && snapshot.width > 0 ? snapshot.width : 0
  const snapshotH = typeof snapshot?.height === 'number' && snapshot.height > 0 ? snapshot.height : 0
  // 快照只有 "WxH" 尺寸串时也能推出宽高
  let w = snapshotW
  let h = snapshotH
  if (!(w > 0 && h > 0) && snapshotSize) {
    const m = snapshotSize.match(/^(\d+)\s*[x×]\s*(\d+)$/i)
    if (m) {
      w = parseInt(m[1]!, 10)
      h = parseInt(m[2]!, 10)
    }
  }

  if (modelDimensionMode === 'aspect_ratio') {
    if (snapshotAspect && snapshotAspect.toLowerCase() !== 'auto') {
      return { ok: true, dimensionMode: 'aspect_ratio', aspectRatio: snapshotAspect, sizeValue: '' }
    }
    const derived = deriveAspectRatioFromDimensions(w, h, aspectOptions)
    if (derived) {
      return { ok: true, dimensionMode: 'aspect_ratio', aspectRatio: derived, sizeValue: '' }
    }
    if (w > 0 && h > 0) {
      return {
        ok: false,
        message: `本轮快照为精确尺寸 ${w}×${h}，当前模型已改为比例出图且无可匹配比例，无法按原画幅追加；请用当前模型重新生成`,
      }
    }
    return {
      ok: false,
      message: '本轮缺少画幅快照，无法按原画幅追加；请用当前模型重新生成',
    }
  }

  // size 模式
  if (snapshotSize && snapshotSize.toLowerCase() !== 'auto') {
    // 模型仍有尺寸白名单时校验；无清单（如后端未下发）则信任快照
    if (!sizeOptions || sizeOptions.length === 0 || sizeOptions.some((o) => o.value === snapshotSize)) {
      return { ok: true, dimensionMode: 'size', aspectRatio: '', sizeValue: snapshotSize }
    }
  }
  const derivedSize = deriveSizeValueFromDimensions(w, h, sizeOptions)
  if (derivedSize) {
    return { ok: true, dimensionMode: 'size', aspectRatio: '', sizeValue: derivedSize }
  }
  if (w > 0 && h > 0) {
    return {
      ok: false,
      message: `本轮快照尺寸 ${w}×${h}（或 ${snapshotSize}）不在当前模型尺寸列表中，无法按原画幅追加；请用当前模型重新生成`,
    }
  }
  return {
    ok: false,
    message: '本轮缺少画幅快照，无法按原画幅追加；请用当前模型重新生成',
  }
}
